import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Landmark, ChevronDown, ChevronRight, FileText, X } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (v) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(Number(v) || 0);
const NONE = "(Belirtilmemiş)";
const NONE_KEY = "__none__";

function usePdfDownload(pdfQuery) {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/gider-avans/mahkeme-ozet/pdf${pdfQuery}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "mahkeme_icra_dairesi_ozeti.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF indirilemedi");
    } finally {
      setDownloading(false);
    }
  };
  return [downloading, download];
}

export function MahkemeOzetCard({ rows, active, onSelect, pdfQuery }) {
  const [open, setOpen] = useState(false);
  const [downloading, downloadPdf] = usePdfDownload(pdfQuery);
  const groups = useMemo(() => {
    const acc = {};
    rows.forEach((r) => {
      const name = String(r.mahkeme_icra_dairesi || "").trim() || NONE;
      const g = (acc[name] ||= { name, count: 0, yatirilan: 0, iade: 0, fark: 0 });
      g.count += 1;
      g.yatirilan += Number(r.yatirilan_avans) || 0;
      g.iade += Number(r.iade_gelen_tutar) || 0;
      g.fark += Number(r.fark) || 0;
    });
    return Object.values(acc).sort((a, b) => (a.name === NONE) - (b.name === NONE) || b.yatirilan - a.yatirilan);
  }, [rows]);
  const named = groups.filter((g) => g.name !== NONE).length;
  const isActive = (g) => active === g.name || (active === NONE_KEY && g.name === NONE);

  return (
    <div className="bg-white border border-slate-200 rounded-xl" data-testid="gider-mahkeme-ozet">
      <div className="flex items-center gap-2 px-4 py-2.5 flex-wrap">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="gider-mahkeme-ozet-toggle"
          className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity">
          {open ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
          <Landmark size={14} className="text-indigo-600" />
          <span className="text-sm font-bold text-slate-900">Mahkeme / İcra Dairesine Göre</span>
          <span className="text-xs text-slate-500">{named} daire · listedeki filtrelere göre · daireye tıklayınca liste süzülür</span>
        </button>
        {active && (
          <button type="button" onClick={() => onSelect(active)} data-testid="gider-mahkeme-filter-clear"
            className="inline-flex items-center gap-1 bg-indigo-600 text-white text-[11px] font-semibold pl-2 pr-1.5 py-0.5 rounded-full hover:bg-indigo-700 transition-colors">
            Daire: {active === NONE_KEY ? NONE : active} <X size={11} />
          </button>
        )}
        <button type="button" onClick={downloadPdf} disabled={downloading} data-testid="gider-mahkeme-ozet-pdf-btn"
          className="ml-auto inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors">
          <FileText size={12} /> {downloading ? "Hazırlanıyor..." : "PDF Rapor"}
        </button>
      </div>
      {open && (
        <div className="px-4 pb-3 overflow-x-auto">
          {groups.length === 0 ? (
            <div className="text-xs text-slate-400 py-2">Kayıt yok</div>
          ) : (
            <table className="w-full text-xs" data-testid="gider-mahkeme-ozet-table">
              <thead>
                <tr className="text-slate-500 border-b border-slate-100">
                  <th className="text-left font-semibold py-1.5">Mahkeme / İcra Dairesi</th>
                  <th className="text-right font-semibold py-1.5 px-2">Kayıt</th>
                  <th className="text-right font-semibold py-1.5 px-2">Yatırılan</th>
                  <th className="text-right font-semibold py-1.5 px-2">İade</th>
                  <th className="text-right font-semibold py-1.5 pl-2">Fark</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {groups.map((g, i) => (
                  <tr key={g.name} onClick={() => onSelect(g.name === NONE ? NONE_KEY : g.name)} title="Bu dairenin kayıtlarını listele"
                    className={`cursor-pointer transition-colors hover:bg-indigo-50 ${g.name === NONE ? "text-slate-400 italic" : "text-slate-800"} ${isActive(g) ? "bg-indigo-50 font-semibold" : ""}`}
                    data-testid={`gider-mahkeme-ozet-row-${i}`}>
                    <td className="py-1.5 px-1 max-w-[320px] truncate" title={g.name}>{g.name}</td>
                    <td className="py-1.5 px-2 text-right font-mono-tab">{g.count}</td>
                    <td className="py-1.5 px-2 text-right font-mono-tab whitespace-nowrap">{fmt(g.yatirilan)}</td>
                    <td className="py-1.5 px-2 text-right font-mono-tab whitespace-nowrap">{fmt(g.iade)}</td>
                    <td className={`py-1.5 pl-2 text-right font-mono-tab font-semibold whitespace-nowrap ${g.fark < 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmt(g.fark)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
